import React, { useState, useEffect, useCallback } from 'react';
import { examApi } from '../api';
import { useAppBackHandler } from '../navigation/AppNavigation';

interface Props {
  onClose: () => void;
  onOpenMBTI?: () => void;
  onOpenMissions?: () => void;
  initialCategory?: string;
  initialPathwayId?: string;
  autoStart?: boolean;
}

interface Question {
  id: number;
  question: string;
  options: string[];
  difficulty?: number;
}

interface QuizData {
  category: string;
  pathway_id?: string | null;
  pathway_name?: string | null;
  questions: Question[];
  total_questions: number | null;
  exam_token: string;
  time_limit_seconds: number;
  expires_at: string;
}

interface ResultDetail {
  question_id: number;
  question: string;
  options: string[];
  chosen: number;
  correct: number;
  is_correct: boolean;
  explanation: string;
}

interface QuizResult {
  score: number;
  total_questions: number;
  score_percent: number;
  time_spent_seconds: number;
  terminated?: boolean;
  pathway_id?: string | null;
  pathway_name?: string | null;
  pathway_exp_gained?: number;
  pathway_exp_eligible?: boolean;
  pathway_exp_min_percent?: number;
  pathway_total_exp?: number | null;
  intellect_score?: number;
  intellect_delta?: number;
  details: ResultDetail[];
}

interface AnswerFeedback {
  question_id: number;
  chosen: number;
  correct: number;
  is_correct: boolean;
  explanation: string;
}

interface DomainInfo {
  key: string; name: string; icon: string; color: string;
  correct: number; total: number; attempts: number;
  percent: number; level: string;
}

interface CategoryInfo {
  key: string; name: string; icon: string; color: string;
  description: string; question_count: number;
}

interface PracticePathway {
  id: string;
  name: string;
  icon: string;
  color: string;
  current_sequence: number;
  total_exp: number;
  question_count: number;
  category_keys: string[];
}

interface DashboardData {
  total_attempts: number;
  total_questions: number;
  total_correct: number;
  overall_percent: number;
  study_streak: number;
  strengths: DomainInfo[];
  weaknesses: DomainInfo[];
  not_tried: DomainInfo[];
  domains: DomainInfo[];
  bank_total?: number;
  bank_by_category?: CategoryInfo[];
  questions_per_quiz?: number;
  recent_exams: {
    id: number; category: string; category_name: string; category_icon: string;
    score: number; total_questions: number; score_percent: number; created_at: string;
  }[];
}

function buildStudyTips(d: DashboardData): string[] {
  const tips: string[] = [];

  if ((d.not_tried || []).length > 0) {
    const names = d.not_tried.slice(0, 3).map(x => x.name).join('、');
    tips.push(`还有 ${d.not_tried.length} 个领域未探索：${names}，可专攻试试。`);
  }
  if ((d.weaknesses || []).length > 0) {
    const names = d.weaknesses.slice(0, 2).map(x => x.name).join('、');
    tips.push(`建议加强：${names}，多练可提升智维度正确率。`);
  }
  if (d.study_streak === 0) {
    tips.push('今天还没答题，做一套题即可计入连续学习天数。');
  } else if (d.study_streak >= 3) {
    tips.push(`已连续学习 ${d.study_streak} 天，保持节奏！`);
  }
  if (d.total_attempts === 0) {
    tips.push('首次考试会建立你的领域掌握档案，选「综合」或单一领域均可。');
  }
  tips.push('选题后即时显示正确答案与解析，适合碎片化学习。');
  return tips.slice(0, 5);
}

type ViewState =
  | { type: 'starting' }
  | { type: 'dashboard' }
  | { type: 'quiz'; data: QuizData }
  | { type: 'result'; result: QuizResult };

const mmss = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const AttributeBoard: React.FC<{ dashboard: DashboardData | null; loading: boolean }> = ({ dashboard, loading }) => {
  const d = dashboard;
  if (loading && !d) {
    return (
      <div className="kw-board-inner">
        <div className="kw-loading"><div className="kw-spinner" /><p>同步属性…</p></div>
      </div>
    );
  }
  if (!d) {
    return <div className="kw-board-inner"><p className="kw-board-empty">暂无数据</p></div>;
  }

  const score = d.overall_percent ?? 0;
  const tips = buildStudyTips(d);

  return (
    <div className="kw-board-inner">
      <div className="kw-board-head">
        <h2>属性看板</h2>
        <span className="kw-board-tag">实时</span>
      </div>

      <div className="kw-board-block">
        <h3>练习提醒</h3>
        <ul className="kw-tip-list">
          {tips.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </div>

      <div className="kw-board-hero">
        <span className="kw-board-hero-lbl">综合掌握度</span>
        <div className="kw-board-hero-score">{score}<small>%</small></div>
        <div className="kw-board-mini-stats">
          <div><strong>{d.total_attempts}</strong><span>场次</span></div>
          <div><strong>{d.total_questions}</strong><span>答题</span></div>
          <div><strong>{d.total_correct}</strong><span>正确</span></div>
          <div><strong>{d.study_streak}</strong><span>连续天</span></div>
        </div>
      </div>

      <div className="kw-board-block">
        <h3>领域掌握度</h3>
        <div className="kw-domains compact">
          {(d.domains || []).map(dm => (
            <div key={dm.key} className="kw-domain">
              <div className="kw-dhdr">
                <span className="kw-di">{dm.icon}</span>
                <span className="kw-dn">{dm.name}</span>
                <span className="kw-dlvl" style={{ color: dm.color }}>{dm.level}</span>
              </div>
              <div className="kw-dbar-track">
                <div className="kw-dbar-fill" style={{ width: `${dm.percent}%`, background: dm.color }} />
              </div>
              <div className="kw-dstats">
                <span>{dm.correct}/{dm.total}</span>
                <span>{dm.attempts} 次</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {((d.strengths || []).length > 0 || (d.weaknesses || []).length > 0) && (
        <div className="kw-board-block">
          <h3>强弱分布</h3>
          <div className="kw-chip-row">
            {(d.strengths || []).map(s => (
              <span key={s.key} className="kw-chip good" style={{ borderColor: `${s.color}40`, background: `${s.color}12` }}>
                {s.icon} {s.name} {s.percent}%
              </span>
            ))}
            {(d.weaknesses || []).map(s => (
              <span key={s.key} className="kw-chip bad">
                {s.icon} {s.name} {s.percent}%
              </span>
            ))}
          </div>
        </div>
      )}

      {(d.not_tried || []).length > 0 && (
        <div className="kw-board-block">
          <h3>未探索</h3>
          <div className="kw-chip-row">
            {d.not_tried.map(s => (
              <span key={s.key} className="kw-chip muted">{s.icon} {s.name}</span>
            ))}
          </div>
        </div>
      )}

      {(d.recent_exams || []).length > 0 && (
        <div className="kw-board-block">
          <h3>最近成绩</h3>
          {d.recent_exams.map(e => (
            <div key={e.id} className="kw-row compact">
              <div className="kw-rl">
                <span className="kw-ri">{e.category_icon}</span>
                <div>
                  <div className="kw-rc">{e.category_name}</div>
                  <div className="kw-rd">{e.created_at?.slice(0, 16).replace('T', ' ')}</div>
                </div>
              </div>
              <div className={`kw-rr ${e.score_percent >= 80 ? 'g' : e.score_percent >= 60 ? 'y' : 'r'}`}>
                {e.score_percent}%
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ExamCenter: React.FC<Props> = ({
  onClose,
  initialCategory = 'mixed',
  initialPathwayId = '',
  autoStart = false,
}) => {
  const [view, setView] = useState<ViewState>(autoStart ? { type: 'starting' } : { type: 'dashboard' });
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [practicePathways, setPracticePathways] = useState<PracticePathway[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedPathway, setSelectedPathway] = useState(initialPathwayId);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [answerFeedback, setAnswerFeedback] = useState<Record<string, AnswerFeedback>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [checkingQuestionId, setCheckingQuestionId] = useState<number | null>(null);
  const [loadingNext, setLoadingNext] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, catRes, pathwayRes] = await Promise.all([
        examApi.getDashboard(),
        examApi.getCategories().catch(() => ({ data: [] })),
        examApi.getPracticePathways().catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes.data);
      setPracticePathways(Array.isArray(pathwayRes.data) ? pathwayRes.data : []);
      const bankCats = dashRes.data?.bank_by_category;
      if (Array.isArray(bankCats) && bankCats.length) {
        const mixedTotal = dashRes.data.bank_total || bankCats.reduce((s, c) => s + (c.question_count || 0), 0);
        setCategories([
          ...bankCats.map(c => ({ ...c, description: c.description || '' })),
          {
            key: 'mixed',
            name: '综合知识',
            icon: '🎯',
            color: '#8b5cf6',
            description: '随机从所有领域抽题',
            question_count: mixedTotal,
          },
        ]);
      } else if (Array.isArray(catRes.data) && catRes.data.length) {
        setCategories(catRes.data);
      }
    } catch (e: any) {
      console.error('加载失败', e);
      const msg = e?.response?.data?.detail || e?.message || '未知错误';
      alert('工作台加载失败: ' + msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const compatiblePathways = practicePathways.filter(pathway =>
    selectedCategory === 'mixed' || pathway.category_keys.includes(selectedCategory)
  );

  useEffect(() => {
    if (selectedPathway && !compatiblePathways.some(pathway => pathway.id === selectedPathway)) {
      setSelectedPathway('');
    }
  }, [selectedCategory, selectedPathway, compatiblePathways]);

  const submitQuiz = useCallback(async (terminated = false) => {
    if (view.type !== 'quiz') return;
    const answered = Object.keys(answers).length;
    if (answered === 0) {
      if (terminated) {
        setAnswers({});
        onClose();
      }
      return;
    }
    setSubmitting(true);
    try {
      const res = await examApi.submitQuiz({
        category: view.data.category,
        pathway_id: view.data.pathway_id || undefined,
        answers,
        exam_token: view.data.exam_token,
      });
      setView({ type: 'result', result: { ...res.data, terminated } });
      const d = await examApi.getDashboard();
      setDashboard(d.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || '提交失败');
    } finally {
      setSubmitting(false);
    }
  }, [view, answers, onClose]);

  const startQuiz = useCallback(async (
    category = selectedCategory,
    pathwayId = selectedPathway,
    closeOnFailure = false,
  ) => {
    try {
      setLoading(true);
      const res = await examApi.getQuiz(category, pathwayId || undefined);
      if (!res.data || !Array.isArray(res.data.questions) || !res.data.questions.length) {
        alert('获取题目失败，请重试');
        if (closeOnFailure) onClose();
        return;
      }
      setAnswers({});
      setAnswerFeedback({});
      setCurrentIndex(0);
      setView({ type: 'quiz', data: res.data });
    } catch (e: any) {
      alert(e.response?.data?.detail || '获取题目失败');
      if (closeOnFailure) onClose();
    } finally {
      setLoading(false);
    }
  }, [onClose, selectedCategory, selectedPathway]);

  useEffect(() => {
    if (!autoStart) return;
    const timer = window.setTimeout(() => {
      startQuiz(initialCategory, initialPathwayId, true);
    }, 0);
    return () => window.clearTimeout(timer);
    // startQuiz intentionally omitted: launch inputs define this one-time entry action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, initialCategory, initialPathwayId]);

  const chooseAnswer = async (question: Question, chosen: number) => {
    if (view.type !== 'quiz' || answerFeedback[String(question.id)]) return;
    setCheckingQuestionId(question.id);
    try {
      const response = await examApi.checkAnswer({
        category: view.data.category,
        pathway_id: view.data.pathway_id || undefined,
        exam_token: view.data.exam_token,
        question_id: question.id,
        chosen,
      });
      setAnswers(prev => ({ ...prev, [String(question.id)]: chosen }));
      setAnswerFeedback(prev => ({ ...prev, [String(question.id)]: response.data }));
    } catch (e: any) {
      alert(e.response?.data?.detail || '答案判定失败');
    } finally {
      setCheckingQuestionId(null);
    }
  };

  const continuePractice = async () => {
    if (view.type !== 'quiz') return;
    setLoadingNext(true);
    try {
      const response = await examApi.getNextQuestion({
        category: view.data.category,
        pathway_id: view.data.pathway_id || undefined,
        exam_token: view.data.exam_token,
        seen_ids: view.data.questions.map(question => question.id),
      });
      const nextQuestion = response.data?.question as Question | null;
      if (!nextQuestion) {
        await submitQuiz(false);
        return;
      }
      const nextQuestions = [...view.data.questions, nextQuestion];
      setView({ type: 'quiz', data: { ...view.data, questions: nextQuestions } });
      setCurrentIndex(nextQuestions.length - 1);
    } catch (e: any) {
      alert(e.response?.data?.detail || '获取下一题失败');
    } finally {
      setLoadingNext(false);
    }
  };

  const exitQuiz = () => {
    if (view.type !== 'quiz') return;
    const answered = Object.keys(answers).length;
    if (answered > 0) {
      if (!window.confirm(`已作答 ${answered} 题，确定放弃本次考试吗？`)) return;
    }
    setAnswers({});
    onClose();
  };

  useAppBackHandler(() => {
    if (view.type !== 'quiz') return false;
    const answered = Object.keys(answers).length;
    if (answered > 0 && !window.confirm(`已作答 ${answered} 题，确定放弃本次考试吗？`)) return true;
    setAnswers({});
    return false;
  });

  const renderStage = () => {
    if (view.type === 'starting') {
      return (
        <div className="kw-stage-inner kw-starting">
          <div className="kw-spinner" />
          <strong>正在生成本次题目</strong>
          <span>{initialCategory === 'mixed' ? '综合知识' : '专项练习'}</span>
        </div>
      );
    }

    if (view.type === 'result') {
      const r = view.result;
      return (
        <div className="kw-stage-inner">
          <div className="kw-stage-head">
            <h2>📊 本次结果</h2>
            {r.terminated && <span className="kw-stage-tag warn">提前终止</span>}
          </div>
          <div className="kw-result-h compact">
            <div className={`kw-result-s ${r.score_percent >= 80 ? 'g' : r.score_percent >= 60 ? 'y' : 'r'}`}>
              {r.score_percent}%
            </div>
            <div className="kw-result-l">{r.score}/{r.total_questions} 正确 · 用时 {mmss(r.time_spent_seconds)}</div>
            {typeof r.intellect_score === 'number' && (
              <div className="kw-intellect-gain">
                智 {r.intellect_score}
                <span>{(r.intellect_delta || 0) >= 0 ? '+' : ''}{r.intellect_delta || 0}</span>
              </div>
            )}
          </div>
          {r.pathway_id && (
            <div className={`kw-pathway-result ${(r.pathway_exp_gained || 0) > 0 ? 'earned' : ''}`}>
              <div>
                <span>本次修炼</span>
                <strong>{r.pathway_name}</strong>
              </div>
              {(r.pathway_exp_gained || 0) > 0 ? (
                <b>+{r.pathway_exp_gained} EXP</b>
              ) : (
                <small>正确率达到 {r.pathway_exp_min_percent || 60}% 后获得途径经验</small>
              )}
            </div>
          )}
          <div className="kw-review-scroll">
            {(r.details || []).map((d, i) => (
              <div key={d.question_id} className={`kw-detail ${d.is_correct ? 'ok' : 'no'}`}>
                <div className="kw-dh">
                  <span>第 {i + 1} 题</span>
                  <span className={`kw-db ${d.is_correct ? 'ok' : 'no'}`}>{d.is_correct ? '✓' : '✗'}</span>
                </div>
                <div className="kw-dq">{d.question}</div>
                {d.explanation && <div className="kw-de">💡 {d.explanation}</div>}
              </div>
            ))}
          </div>
          <div className="kw-run-foot">
            <button type="button" className="kw-btn p" onClick={() => startQuiz()}>再来一组</button>
            <button type="button" className="kw-btn" onClick={onClose}>返回智主页</button>
          </div>
        </div>
      );
    }

    if (view.type === 'quiz') {
      const qd = view.data;
      const questions = qd.questions;
      const q = questions[currentIndex];
      const answeredCount = Object.keys(answers).length;
      const feedback = answerFeedback[String(q.id)];
      const hasAnswer = !!feedback;

      return (
        <div className="kw-stage-inner">
          <div className="kw-stage-head">
            <h2>🎮 答题控制台</h2>
            <div className="kw-quiz-meta">
              {qd.pathway_name && <span className="kw-stage-tag">{qd.pathway_name}</span>}
              <span className="kw-answered-count">已答 {answeredCount} 题</span>
            </div>
          </div>

          <div className="kw-q-card single">
            <div className="kw-q-num">自由练习 · 第 {currentIndex + 1} 题</div>
            <div className="kw-q-text">{q.question}</div>
            <div className="kw-opts">
              {q.options.map((opt, oi) => {
                const chosen = answers[String(q.id)];
                const isChosen = chosen === oi;
                const isCorrect = feedback?.correct === oi;
                let optCls = 'kw-opt';
                if (feedback) {
                  if (isCorrect) optCls += ' correct';
                  else if (isChosen) optCls += ' wrong';
                  else optCls += ' dim';
                } else if (isChosen) {
                  optCls += ' sel';
                }
                return (
                  <button
                    key={oi}
                    type="button"
                    className={optCls}
                    disabled={!!feedback || checkingQuestionId === q.id}
                    onClick={() => chooseAnswer(q, oi)}
                  >
                    <span className="kw-opt-l">{String.fromCharCode(65 + oi)}</span>
                    <span>{opt}</span>
                    {feedback && isCorrect && <span className="kw-opt-mark ok">✓</span>}
                    {feedback && isChosen && !isCorrect && <span className="kw-opt-mark bad">✗</span>}
                  </button>
                );
              })}
            </div>
            {feedback && (
              <div className={`kw-feedback ${feedback.is_correct ? 'ok' : 'bad'}`}>
                <div className="kw-feedback-head">
                  {feedback.is_correct ? '回答正确' : '回答错误'}
                  {!feedback.is_correct && (
                    <span>正确答案：{String.fromCharCode(65 + feedback.correct)}</span>
                  )}
                </div>
                {feedback.explanation && <p>{feedback.explanation}</p>}
              </div>
            )}
          </div>

          <div className="kw-run-foot">
            <button
              type="button"
              className="kw-btn ghost"
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
            >
              上一题
            </button>
            <button
              type="button"
              className="kw-btn p"
              disabled={!hasAnswer || submitting || loadingNext}
              onClick={() => {
                if (currentIndex < questions.length - 1) {
                  setCurrentIndex(i => i + 1);
                } else {
                  continuePractice();
                }
              }}
            >
              {loadingNext ? '取题中…' : '下一题'}
            </button>
            <button type="button" className="kw-btn danger" disabled={submitting || answeredCount === 0} onClick={() => submitQuiz(true)}>
              结束并结算
            </button>
          </div>
        </div>
      );
    }

  const cats = categories.length
    ? categories
    : [{ key: 'mixed', name: '综合', icon: '🎯', color: '#8b5cf6', description: '随机抽题', question_count: 0 }];

  const idleTips = dashboard ? buildStudyTips(dashboard).slice(0, 2) : [];

    return (
      <div className="kw-stage-inner">
        <div className="kw-stage-head">
          <h2>🎮 答题控制台</h2>
          <span className="kw-stage-tag">待机</span>
        </div>

        <p className="kw-stage-desc">
          选择领域后开始答题，限时 10 分钟。可关联已订阅的神之途径，交卷后结算修炼经验。
        </p>

        {idleTips.length > 0 && (
          <div className="kw-console-tips">
            {idleTips.map((t, i) => (
              <p key={i}>💡 {t}</p>
            ))}
          </div>
        )}

        <div className="kw-cat-grid">
          {cats.map(c => (
            <button
              key={c.key}
              type="button"
              className={`kw-cat ${selectedCategory === c.key ? 'sel' : ''}`}
              style={{ '--cat-color': c.color } as React.CSSProperties}
              onClick={() => setSelectedCategory(c.key)}
            >
              <span className="kw-cat-ic">{c.icon}</span>
              <span className="kw-cat-nm">{c.name}</span>
            </button>
          ))}
        </div>

        <div className="kw-pathway-picker">
          <div className="kw-picker-head">
            <div>
              <strong>本次修炼途径</strong>
              <span>可选，达到 60% 正确率后获得途径经验</span>
            </div>
            {selectedPathway && (
              <button type="button" onClick={() => setSelectedPathway('')}>清除</button>
            )}
          </div>
          {compatiblePathways.length > 0 ? (
            <div className="kw-pathway-options">
              {compatiblePathways.map(pathway => (
                <button
                  key={pathway.id}
                  type="button"
                  className={`kw-pathway-option ${selectedPathway === pathway.id ? 'sel' : ''}`}
                  style={{ '--pathway-color': pathway.color } as React.CSSProperties}
                  onClick={() => setSelectedPathway(pathway.id)}
                >
                  <span>{pathway.icon}</span>
                  <div>
                    <strong>{pathway.name}</strong>
                    <small>第 {pathway.current_sequence} 级 · {pathway.question_count} 题可用</small>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="kw-pathway-empty">当前领域没有已订阅的匹配途径，可继续普通答题。</p>
          )}
        </div>

        <button type="button" className="kw-start inline" onClick={() => startQuiz()} disabled={loading}>
          <span className="kw-start-icon">📝</span>
          <div className="kw-start-t">
            <strong>开始答题</strong>
            <small>{selectedPathway ? '答题并积累途径经验' : '普通知识练习'}</small>
          </div>
          <span className="kw-start-arrow">→</span>
        </button>
      </div>
    );
  };

  const isFocusMode = view.type === 'starting' || view.type === 'quiz';

  return (
    <div className="kw-root">
      <header className="kw-header">
        <button type="button" className="kw-back" onClick={view.type === 'quiz' ? exitQuiz : onClose}>
          <BackIcon />
        </button>
        <h1>{isFocusMode ? '知识答题' : '📚 知识工作台'}</h1>
        {isFocusMode ? (
          <span className="kw-header-spacer" />
        ) : (
          <button type="button" className="kw-back" onClick={loadDashboard} title="刷新看板">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" />
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" />
            </svg>
          </button>
        )}
      </header>

      <div className={`kw-console-grid ${isFocusMode ? 'focus' : ''}`}>
        <section className="kw-stage">{renderStage()}</section>
        {!isFocusMode && (
          <aside className="kw-board">
            <AttributeBoard dashboard={dashboard} loading={loading} />
          </aside>
        )}
      </div>

      <style>{kwStyles}</style>
    </div>
  );
};

const kwStyles = `
.kw-root { min-height: 100vh; background: #030712; color: #f8fafc; font-family: inherit; -webkit-tap-highlight-color: transparent; }
.kw-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; padding-top: calc(16px + var(--status-bar-height, 28px)); background: rgba(3,7,18,0.94); border-bottom: 1px solid rgba(96,165,250,0.12); position: sticky; top: 0; z-index: 10; backdrop-filter: blur(14px); }
.kw-header h1 { font-size: 17px; margin: 0; font-weight: 800; }
.kw-back { width: 36px; height: 36px; border-radius: 10px; border: 1px solid rgba(96,165,250,0.2); background: rgba(14,165,233,0.12); color: #e0f2fe; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.kw-header-spacer { width: 36px; height: 36px; }

.kw-console-grid { display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr); gap: 16px; padding: 16px; padding-bottom: 100px; max-width: 1100px; margin: 0 auto; align-items: start; }
.kw-console-grid.focus { display: block; max-width: 820px; padding-top: 24px; }
.kw-console-grid.focus .kw-stage { min-height: min(620px, calc(100vh - 150px)); }
.kw-console-grid.focus .kw-stage-inner { min-height: min(580px, calc(100vh - 190px)); padding: 24px; }
.kw-console-grid.focus .kw-q-card { padding: 24px; }
.kw-console-grid.focus .kw-q-text { font-size: 18px; line-height: 1.65; margin-bottom: 20px; }
.kw-console-grid.focus .kw-opts { gap: 10px; }
.kw-console-grid.focus .kw-opt { min-height: 52px; padding: 13px 14px; font-size: 14px; }
@media (max-width: 860px) { .kw-console-grid { grid-template-columns: 1fr; } }

.kw-stage { background: rgba(15,23,42,0.78); border: 1px solid rgba(96,165,250,0.14); border-radius: 18px; min-height: 420px; }
.kw-stage-inner { padding: 18px; display: flex; flex-direction: column; min-height: 380px; }
.kw-starting { align-items: center; justify-content: center; gap: 8px; color: #94a3b8; }
.kw-starting strong { color: #e0f2fe; font-size: 15px; }
.kw-starting span { color: #64748b; font-size: 11px; }
.kw-stage-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.kw-stage-head h2 { font-size: 14px; margin: 0; font-weight: 800; color: #e0f2fe; letter-spacing: 0.5px; }
.kw-stage-tag { font-size: 10px; font-weight: 700; color: #7dd3fc; padding: 3px 8px; border-radius: 6px; background: rgba(14,165,233,0.12); border: 1px solid rgba(56,189,248,0.25); }
.kw-stage-tag.warn { color: #fbbf24; border-color: rgba(251,191,36,0.35); background: rgba(251,191,36,0.1); }
.kw-stage-desc { font-size: 12px; color: #94a3b8; line-height: 1.5; margin: 0 0 12px; }
.kw-console-tips { margin: 0 0 14px; padding: 10px 12px; border-radius: 10px; background: rgba(14,165,233,0.08); border: 1px solid rgba(56,189,248,0.18); }
.kw-console-tips p { margin: 0; font-size: 11px; line-height: 1.5; color: #7dd3fc; }
.kw-console-tips p + p { margin-top: 6px; }

.kw-tip-list { margin: 0; padding: 0 0 0 16px; }
.kw-tip-list li { font-size: 11px; line-height: 1.55; color: #94a3b8; margin-bottom: 6px; }
.kw-tip-list li:last-child { margin-bottom: 0; }

.kw-board { background: rgba(15,23,42,0.55); border: 1px solid rgba(255,255,255,0.06); border-radius: 18px; position: sticky; top: calc(68px + var(--status-bar-height, 28px)); max-height: calc(100vh - 100px); overflow: auto; }
.kw-board-inner { padding: 16px; }
.kw-board-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.kw-board-head h2 { font-size: 13px; margin: 0; font-weight: 800; color: #e0f2fe; }
.kw-board-tag { font-size: 10px; color: #64748b; }
.kw-board-empty { color: #94a3b8; font-size: 13px; text-align: center; padding: 24px 0; }
.kw-board-hero { background: linear-gradient(145deg, rgba(14,165,233,0.14), rgba(15,23,42,0.7)); border: 1px solid rgba(125,211,252,0.14); border-radius: 14px; padding: 14px; margin-bottom: 14px; }
.kw-board-hero-lbl { font-size: 11px; color: #7dd3fc; font-weight: 700; }
.kw-board-hero-score { font-size: 40px; font-weight: 900; color: #60a5fa; line-height: 1.1; margin: 4px 0 10px; }
.kw-board-hero-score small { font-size: 18px; color: #7dd3fc; }
.kw-board-mini-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.kw-board-mini-stats div { text-align: center; background: rgba(0,0,0,0.22); border-radius: 8px; padding: 8px 2px; }
.kw-board-mini-stats strong { display: block; font-size: 16px; color: #e0f2fe; }
.kw-board-mini-stats span { font-size: 9px; color: #94a3b8; }
.kw-board-block { margin-bottom: 14px; }
.kw-board-block h3 { font-size: 12px; margin: 0 0 10px; color: #cbd5e1; font-weight: 800; }

.kw-loading { text-align: center; padding: 32px 12px; color: #94a3b8; }
.kw-spinner { width: 28px; height: 28px; border: 3px solid rgba(255,255,255,0.1); border-top-color: #38bdf8; border-radius: 50%; animation: kw-spin 0.8s linear infinite; margin: 0 auto 10px; }
@keyframes kw-spin { to { transform: rotate(360deg); } }

.kw-cat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(108px, 1fr)); gap: 8px; margin-bottom: 16px; max-height: 280px; overflow-y: auto; padding-right: 4px; }
.kw-cat { text-align: left; cursor: pointer; background: rgba(2,6,23,0.45); border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; padding: 10px; color: inherit; font-family: inherit; transition: border-color 0.15s; }
.kw-cat.sel { border-color: var(--cat-color, #60a5fa); box-shadow: 0 0 0 1px var(--cat-color, #60a5fa) inset; }
.kw-cat-ic { font-size: 20px; display: block; }
.kw-cat-nm { display: block; font-size: 12px; font-weight: 700; margin-top: 6px; color: #e2e8f0; }

.kw-pathway-picker { margin: 0 0 16px; padding: 12px; border: 1px solid rgba(167,139,250,0.18); background: rgba(76,29,149,0.08); border-radius: 12px; }
.kw-picker-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.kw-picker-head div { min-width: 0; }
.kw-picker-head strong { display: block; color: #ede9fe; font-size: 12px; }
.kw-picker-head span { display: block; color: #94a3b8; font-size: 10px; margin-top: 3px; line-height: 1.4; }
.kw-picker-head button { border: 0; background: transparent; color: #c4b5fd; font-size: 11px; cursor: pointer; padding: 2px 0; }
.kw-pathway-options { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; max-height: 184px; overflow-y: auto; }
.kw-pathway-option { min-width: 0; display: flex; align-items: center; gap: 8px; padding: 9px; border-radius: 9px; border: 1px solid rgba(255,255,255,0.08); background: rgba(2,6,23,0.42); color: inherit; text-align: left; cursor: pointer; font-family: inherit; }
.kw-pathway-option.sel { border-color: var(--pathway-color, #a78bfa); box-shadow: inset 0 0 0 1px var(--pathway-color, #a78bfa); }
.kw-pathway-option > span { flex: 0 0 28px; font-size: 20px; text-align: center; }
.kw-pathway-option div { min-width: 0; }
.kw-pathway-option strong { display: block; color: #e2e8f0; font-size: 11px; line-height: 1.35; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.kw-pathway-option small { display: block; color: #64748b; font-size: 9px; margin-top: 3px; }
.kw-pathway-empty { margin: 0; color: #64748b; font-size: 11px; line-height: 1.45; }
@media (max-width: 520px) { .kw-pathway-options { grid-template-columns: 1fr; } }
@media (max-width: 620px) {
  .kw-console-grid.focus { padding: 12px; padding-bottom: 88px; }
  .kw-console-grid.focus .kw-stage { min-height: calc(100vh - 116px); }
  .kw-console-grid.focus .kw-stage-inner { min-height: calc(100vh - 156px); padding: 16px; }
  .kw-console-grid.focus .kw-q-card { padding: 16px; }
  .kw-console-grid.focus .kw-q-text { font-size: 16px; }
}

.kw-start { display: flex; align-items: center; gap: 12px; width: 100%; padding: 16px; background: linear-gradient(135deg, rgba(14,165,233,0.2), rgba(2,132,199,0.1)); border: 1px solid rgba(56,189,248,0.3); border-radius: 14px; cursor: pointer; text-align: left; color: inherit; font-family: inherit; transition: all 0.15s; }
.kw-start:disabled { opacity: 0.5; cursor: default; }
.kw-start-icon { font-size: 28px; width: 46px; height: 46px; display: flex; align-items: center; justify-content: center; background: rgba(14,165,233,0.15); border-radius: 12px; flex-shrink: 0; }
.kw-start-t { flex: 1; }
.kw-start-t strong { display: block; font-size: 15px; font-weight: 800; color: #e0f2fe; margin-bottom: 2px; }
.kw-start-t small { font-size: 11px; color: #94a3b8; }
.kw-start-arrow { font-size: 18px; color: #38bdf8; font-weight: 700; }

.kw-timer { font-size: 14px; font-weight: 800; color: #60a5fa; font-variant-numeric: tabular-nums; }
.kw-timer.warn { color: #fbbf24; }
.kw-timer.danger { color: #ef4444; animation: kw-pulse 0.5s linear infinite; }
.kw-quiz-meta { display: flex; align-items: center; justify-content: flex-end; gap: 8px; min-width: 0; }
.kw-quiz-meta .kw-stage-tag { max-width: 190px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
@keyframes kw-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

.kw-progress-bar { position: relative; height: 22px; background: rgba(30,41,59,0.8); border-radius: 11px; overflow: hidden; margin-bottom: 12px; }
.kw-progress-fill { height: 100%; background: linear-gradient(90deg, #0284c7, #38bdf8); border-radius: 11px; transition: width 0.3s; }
.kw-progress-text { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; color: #fff; }

.kw-q-dots { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.kw-q-dot { width: 10px; height: 10px; border-radius: 50%; border: none; padding: 0; background: rgba(148,163,184,0.25); cursor: pointer; }
.kw-q-dot.active { background: #38bdf8; box-shadow: 0 0 0 2px rgba(56,189,248,0.35); transform: scale(1.15); }
.kw-q-dot.done { background: #a78bfa; }
.kw-q-dot.active.done { background: #38bdf8; }

.kw-q-card { background: rgba(2,6,23,0.4); border: 1px solid rgba(148,163,184,0.1); border-radius: 14px; padding: 16px; flex: 1; }
.kw-q-card.single { margin-bottom: 0; }
.kw-q-num { font-size: 11px; color: #64748b; font-weight: 700; margin-bottom: 8px; }
.kw-q-text { font-size: 16px; line-height: 1.55; color: #f1f5f9; font-weight: 600; margin-bottom: 14px; }
.kw-opts { display: flex; flex-direction: column; gap: 8px; }
.kw-opt { display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; padding: 11px 12px; border-radius: 10px; border: 1px solid rgba(148,163,184,0.15); background: rgba(30,41,59,0.5); color: #cbd5e1; cursor: pointer; font-family: inherit; font-size: 13px; transition: all 0.15s; }
.kw-opt:disabled { cursor: default; }
.kw-opt.sel { border-color: #38bdf8; background: rgba(14,165,233,0.15); color: #e0f2fe; }
.kw-opt.correct { border-color: #34d399; background: rgba(52,211,153,0.14); color: #d1fae5; }
.kw-opt.wrong { border-color: #f87171; background: rgba(248,113,113,0.14); color: #fecaca; }
.kw-opt.dim { opacity: 0.45; }
.kw-opt-l { width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: rgba(148,163,184,0.12); font-size: 12px; font-weight: 800; flex-shrink: 0; }
.kw-opt.sel .kw-opt-l { background: #0284c7; color: #fff; }
.kw-opt.correct .kw-opt-l { background: #10b981; color: #fff; }
.kw-opt.wrong .kw-opt-l { background: #ef4444; color: #fff; }
.kw-opt-mark { margin-left: auto; font-weight: 900; font-size: 14px; }
.kw-opt-mark.ok { color: #34d399; }
.kw-opt-mark.bad { color: #f87171; }
.kw-feedback { margin-top: 14px; border-radius: 12px; padding: 12px 14px; font-size: 12px; line-height: 1.55; animation: kw-fade-in 0.2s ease; }
.kw-feedback.ok { background: rgba(52,211,153,0.1); border: 1px solid rgba(52,211,153,0.25); color: #a7f3d0; }
.kw-feedback.bad { background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.25); color: #fecaca; }
.kw-feedback-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-weight: 800; margin-bottom: 6px; color: inherit; }
.kw-feedback-head span { font-size: 11px; font-weight: 700; opacity: 0.9; }
.kw-feedback p { margin: 0; color: rgba(226,232,240,0.85); }
@keyframes kw-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }

.kw-run-foot { display: flex; gap: 8px; margin-top: 16px; flex-wrap: wrap; }
.kw-btn { flex: 1; min-width: 88px; padding: 11px 10px; border-radius: 11px; border: none; font-weight: 800; cursor: pointer; font-family: inherit; text-align: center; font-size: 13px; background: rgba(30,41,59,0.86); color: #bae6fd; border: 1px solid rgba(96,165,250,0.18); }
.kw-btn:disabled { opacity: 0.4; cursor: default; }
.kw-btn.p { background: linear-gradient(135deg, #0284c7, #0891b2); color: #fff; border: none; }
.kw-btn.ghost { flex: 0 0 auto; min-width: 72px; }
.kw-btn.danger { flex: 0 0 auto; min-width: 88px; background: rgba(127,29,29,0.35); color: #fca5a5; border: 1px solid rgba(248,113,113,0.3); }

.kw-domains { display: flex; flex-direction: column; gap: 10px; }
.kw-domains.compact .kw-domain { padding: 10px; }
.kw-domain { background: rgba(2,6,23,0.35); border: 1px solid rgba(148,163,184,0.08); border-radius: 12px; padding: 12px; }
.kw-dhdr { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.kw-di { font-size: 16px; }
.kw-dn { flex: 1; font-size: 12px; font-weight: 700; color: #e2e8f0; }
.kw-dlvl { font-size: 10px; font-weight: 700; }
.kw-dbar-track { height: 5px; background: rgba(30,41,59,0.8); border-radius: 999px; overflow: hidden; margin-bottom: 4px; }
.kw-dbar-fill { height: 100%; border-radius: 999px; transition: width 0.8s ease; }
.kw-dstats { display: flex; justify-content: space-between; font-size: 10px; color: #64748b; }

.kw-chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.kw-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 999px; border: 1px solid; font-size: 11px; font-weight: 600; }
.kw-chip.good { color: #6ee7b7; border-color: rgba(52,211,153,0.3); background: rgba(52,211,153,0.1); }
.kw-chip.bad { color: #fca5a5; border-color: rgba(248,113,113,0.3); background: rgba(248,113,113,0.08); }
.kw-chip.muted { color: #94a3b8; border-color: rgba(148,163,184,0.2); background: rgba(148,163,184,0.05); }

.kw-row { display: flex; align-items: center; justify-content: space-between; background: rgba(2,6,23,0.35); border: 1px solid rgba(148,163,184,0.08); border-radius: 10px; padding: 10px; margin-bottom: 6px; }
.kw-row.compact { padding: 8px 10px; }
.kw-rl { display: flex; align-items: center; gap: 8px; min-width: 0; }
.kw-ri { font-size: 16px; }
.kw-rc { font-size: 12px; font-weight: 700; color: #e2e8f0; }
.kw-rd { font-size: 10px; color: #64748b; }
.kw-rr { font-size: 16px; font-weight: 900; }
.kw-rr.g { color: #34d399; }
.kw-rr.y { color: #fbbf24; }
.kw-rr.r { color: #f87171; }

.kw-result-h.compact { text-align: center; padding: 12px 0 16px; }
.kw-result-s { font-size: 56px; font-weight: 900; line-height: 1; }
.kw-result-s.g { color: #34d399; }
.kw-result-s.y { color: #fbbf24; }
.kw-result-s.r { color: #f87171; }
.kw-result-l { font-size: 13px; color: #94a3b8; margin-top: 6px; }
.kw-intellect-gain { width: max-content; margin: 10px auto 0; padding: 6px 11px; border-radius: 999px; background: rgba(20,184,166,0.12); border: 1px solid rgba(94,234,212,0.22); color: #ccfbf1; font-size: 12px; font-weight: 850; }
.kw-intellect-gain span { margin-left: 7px; color: #5eead4; }
.kw-pathway-result { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px; margin: 0 0 12px; border-radius: 10px; border: 1px solid rgba(167,139,250,0.2); background: rgba(76,29,149,0.1); }
.kw-pathway-result div { min-width: 0; }
.kw-pathway-result span { display: block; color: #a78bfa; font-size: 10px; margin-bottom: 3px; }
.kw-pathway-result strong { display: block; color: #ede9fe; font-size: 12px; overflow-wrap: anywhere; }
.kw-pathway-result b { color: #fbbf24; font-size: 18px; white-space: nowrap; }
.kw-pathway-result small { color: #94a3b8; font-size: 10px; line-height: 1.4; text-align: right; max-width: 180px; }
.kw-pathway-result.earned { border-color: rgba(251,191,36,0.28); background: rgba(120,53,15,0.1); }
.kw-review-scroll { flex: 1; overflow-y: auto; max-height: 280px; margin-bottom: 8px; }
.kw-detail { background: rgba(2,6,23,0.4); border: 1px solid; border-radius: 12px; padding: 12px; margin-bottom: 8px; }
.kw-detail.ok { border-color: rgba(52,211,153,0.2); border-left: 3px solid #34d399; }
.kw-detail.no { border-color: rgba(248,113,113,0.2); border-left: 3px solid #f87171; }
.kw-dh { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 11px; color: #64748b; font-weight: 700; }
.kw-db.ok { color: #34d399; }
.kw-db.no { color: #f87171; }
.kw-dq { font-size: 13px; color: #e2e8f0; font-weight: 600; margin-bottom: 6px; }
.kw-de { font-size: 12px; color: #7dd3fc; line-height: 1.5; }
`;

export default ExamCenter;
